import { useCallback, useEffect, useRef, useState } from 'react';
import { eventIcs, googleCalendarUrl, type CalendarAccount, type CalendarInfo, type CalendarInvite, type CalendarSource, type EventDraft, type EventSuggestion, type RsvpStatus } from '@momomail/shared';
import { api, downloadText, errorText } from '../lib/bridge';
import { timeAgo } from '../lib/format';
import { language, locale, t, tMessage } from '../lib/i18n';
import { startGoogleFlow, useGoogleInfo } from './Google';
import { Icon } from './Icon';
import { confirmDialog, Dialog } from './Overlay';
import { toast } from './Toasts';

/**
 * Calendar: the meeting invite card in emails (accept / tentative / decline), the "加入日历" (add to calendar) dialog, and the Google Calendar connection in settings.
 * Time format matches the backend: all-day is 'YYYY-MM-DD' (end date exclusive), otherwise a UTC ISO string; the UI displays and edits in the local time zone.
 */

const localZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
const pad = (n: number) => String(n).padStart(2, '0');

/** Add days to a 'YYYY-MM-DD'. */
function addDays(date: string, days: number): string {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);
}

/** ISO → value for a datetime-local input (local time zone). */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO → local date 'YYYY-MM-DD'. */
function localDate(iso: string): string {
  return toLocalInput(iso).slice(0, 10);
}

const WEEK = ['日', '一', '二', '三', '四', '五', '六']; // i18n-ok for Chinese dates
function dayLabel(d: Date): string {
  const sameYear = d.getFullYear() === new Date().getFullYear();
  if (language() !== 'zh') return d.toLocaleDateString(locale(), { weekday: 'short', month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  return `${sameYear ? '' : `${d.getFullYear()}年`}${d.getMonth() + 1}月${d.getDate()}日 周${WEEK[d.getDay()]}`; // i18n-ok
}
const clock = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

/** How to describe a time span: all day, same day, multi-day. */
export function rangeText(allDay: boolean, start: string, end: string): string {
  if (allDay) {
    const s = new Date(`${start}T00:00:00`);
    const last = addDays(end, -1);
    if (last <= start) return t('{range}（全天）', { range: dayLabel(s) });
    return t('{range}（全天）', { range: `${dayLabel(s)} – ${dayLabel(new Date(`${last}T00:00:00`))}` });
  }
  const s = new Date(start);
  const e = new Date(end);
  if (+e <= +s) return `${dayLabel(s)} ${clock(s)}`;
  if (s.toDateString() === e.toDateString()) return `${dayLabel(s)} ${clock(s)}–${clock(e)}`;
  return `${dayLabel(s)} ${clock(s)} – ${dayLabel(e)} ${clock(e)}`;
}

const RSVP = (): { status: RsvpStatus; label: string }[] => [
  { status: 'accepted', label: t('接受') },
  { status: 'tentative', label: t('暂定') },
  { status: 'declined', label: t('拒绝') },
];
const RSVP_DONE = (): Record<RsvpStatus, string> => ({ accepted: t('已接受'), tentative: t('已回复暂定'), declined: t('已拒绝') });
const RSVP_MINE = (): Record<RsvpStatus, string> => ({ accepted: t('你已接受'), tentative: t('你已回复暂定'), declined: t('你已拒绝') });

// ---------------- Invite card ----------------

export function InviteCard({ messageId, invite }: { messageId: number; invite: CalendarInvite }) {
  const [mine, setMine] = useState<RsvpStatus | null>(invite.myStatus);
  const [busy, setBusy] = useState<RsvpStatus | null>(null);
  const [more, setMore] = useState(false);
  useEffect(() => setMine(invite.myStatus), [invite.myStatus]);

  const respond = async (status: RsvpStatus) => {
    setBusy(status);
    try {
      const r = await api().calendar.respond(messageId, status);
      setMine(status);
      const cal = r.calendar === 'added' ? t('，已加入 Google 日历') : r.calendar === 'updated' ? t('，已更新 Google 日历') : '';
      toast(t('{done}，回复已发给组织者{cal}', { done: RSVP_DONE()[status], cal }), 'ok');
      if (r.warning) toast(tMessage(r.warning), 'info');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(null);
    }
  };

  const zone = localZone();
  const otherZone = invite.timeZone && invite.timeZone !== zone ? invite.timeZone : null;
  const others = invite.attendees.length;
  return (
    <section className={`invite ${invite.cancelled ? 'invite--cancelled' : ''}`} aria-label={t('会议邀请')}>
      <div className="invite__badge" aria-hidden>
        <Icon name="calendar" size={20} />
      </div>
      <div className="invite__body">
        <div className="invite__title">
          <strong>{invite.summary || t('（无标题的活动）')}</strong>
          {invite.recurring && <span className="tag tag--dim">{t('重复')}</span>}
          {invite.method === 'REQUEST' && !invite.cancelled && <span className="tag tag--dim">{t('邀请')}</span>}
        </div>
        {invite.cancelled && (
          <div className="notice notice--error invite__cancel">
            <Icon name="alert" size={14} /> {t('这个活动已经取消了。')}
          </div>
        )}
        <div className="invite__line">
          <Icon name="clock" size={14} />
          <span>
            {rangeText(invite.allDay, invite.start, invite.end)}
            {otherZone && <span className="muted">{t('（邀请里的时区：{zone}）', { zone: otherZone })}</span>}
            {invite.recurring && <span className="muted">{t(' · 第一次')}</span>}
          </span>
        </div>
        {invite.location && (
          <div className="invite__line">
            <Icon name="pin" size={14} />
            <span className="invite__wrap">{invite.location}</span>
          </div>
        )}
        {invite.organizer && (
          <div className="invite__line">
            <Icon name="user" size={14} />
            <span>
              {t('组织者：{name}', { name: invite.organizer.name || invite.organizer.address })}
              {others > 0 && <span className="muted">{t(' · {n} 位参与者', { n: others })}</span>}
            </span>
          </div>
        )}
        {invite.description && (
          <div className="invite__desc">
            <button className="bare invite__more" onClick={() => setMore((v) => !v)} aria-expanded={more}>
              <Icon name="down" size={13} className={more ? 'is-open' : ''} /> {t('说明')}
            </button>
            {more && <p className="invite__wrap">{invite.description}</p>}
          </div>
        )}
        <div className="invite__actions">
          {invite.canRespond && (
            <div className="segmented invite__rsvp" role="group" aria-label={t('回复邀请')}>
              {RSVP().map((r) => (
                <button key={r.status} aria-pressed={mine === r.status} disabled={!!busy} onClick={() => void respond(r.status)}>
                  {busy === r.status ? <span className="spinner" /> : r.label}
                </button>
              ))}
            </div>
          )}
          {mine && <span className="muted invite__mine">{RSVP_MINE()[mine]}</span>}
          <span className="compose__spacer" />
          {!invite.cancelled && (
            <button
              className="btn btn--small btn--quiet"
              onClick={() =>
                openAddToCalendar({
                  messageId,
                  event: { title: invite.summary || t('（无标题）'), location: invite.location, description: invite.description, allDay: invite.allDay, start: invite.start, end: invite.end, timeZone: zone },
                })
              }
            >
              <Icon name="calendarPlus" size={15} /> {t('加入日历')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

// ---------------- Add to calendar ----------------

interface AddRequest {
  messageId: number | null;
  /** Known event (invite); if absent, let the backend guess from the email. */
  event?: EventDraft;
}

let openAdd: ((r: AddRequest) => void) | null = null;

export function openAddToCalendar(request: AddRequest) {
  openAdd?.(request);
}

export function AddToCalendarHost() {
  const [request, setRequest] = useState<AddRequest | null>(null);
  const [shown, setShown] = useState(false);
  const [suggestion, setSuggestion] = useState<EventSuggestion | null>(null);
  const [event, setEvent] = useState<EventDraft | null>(null);
  const [info, setInfo] = useState<CalendarInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ calendar: string; htmlLink: string | null } | null>(null);

  useEffect(() => {
    openAdd = (r) => {
      setRequest(r);
      setSuggestion(null);
      setEvent(r.event ?? null);
      setError(null);
      setSaved(null);
      setShown(true);
    };
    return () => {
      openAdd = null;
    };
  }, []);

  useEffect(() => {
    if (!shown || !request) return;
    let alive = true;
    void api()
      .calendar.info()
      .then((i) => alive && setInfo(i))
      .catch(() => alive && setInfo(null));
    if (!request.event && request.messageId != null) {
      api()
        .calendar.suggest(request.messageId, localZone())
        .then((s) => {
          if (!alive) return;
          setSuggestion(s);
          setEvent(s.event);
        })
        .catch((e) => alive && setError(errorText(e)));
    }
    return () => {
      alive = false;
    };
  }, [shown, request]);

  const close = useCallback(() => setShown(false), []);
  const set = (patch: Partial<EventDraft>) => {
    setSaved(null);
    setEvent((e) => (e ? { ...e, ...patch } : e));
  };

  const toggleAllDay = (allDay: boolean) => {
    if (!event || allDay === event.allDay) return;
    if (allDay) {
      const day = localDate(event.start);
      set({ allDay, start: day, end: addDays(day, 1) });
    } else {
      const start = new Date(`${event.start}T09:00:00`);
      set({ allDay, start: start.toISOString(), end: new Date(+start + 3_600_000).toISOString() });
    }
  };

  const invalid = !event ? t('还没准备好') : !event.title.trim() ? t('请填写标题') : event.allDay ? (event.end <= event.start ? t('结束日期不能早于开始日期') : null) : +new Date(event.end) < +new Date(event.start) ? t('结束时间不能早于开始时间') : null;

  const save = async () => {
    if (!event || invalid) return;
    setBusy(true);
    try {
      const r = await api().calendar.add({ ...event, title: event.title.trim() }, request?.messageId ?? null);
      setSaved(r);
      toast(t('已加入 {calendar} 的 Google 日历', { calendar: r.calendar }), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
      void api()
        .calendar.info()
        .then(setInfo)
        .catch(() => {});
    } finally {
      setBusy(false);
    }
  };

  const link = info?.links.find((l) => !l.error) ?? info?.links[0] ?? null;
  const candidates = suggestion?.candidates ?? [];

  return (
    <Dialog
      open={shown}
      onClose={close}
      title={t('加入日历')}
      labelledBy="add-cal-title"
      className="add-cal"
      footer={
        event && (
          <>
            <button className="btn btn--quiet btn--small" disabled={!!invalid} onClick={() => downloadText(eventIcs(event, `${Date.now()}@momomail`), `${event.title.replace(/[\\/:*?"<>|]/g, '_').slice(0, 60) || 'event'}.ics`, 'text/calendar')}>
              <Icon name="download" size={14} /> {t('下载 .ics')}
            </button>
            <button className="btn btn--small" disabled={!!invalid} onClick={() => void api().mail.openExternal(googleCalendarUrl(event))}>
              <Icon name="external" size={14} /> {t('在 Google 日历中打开')}
            </button>
            {link && (
              <button className="btn btn--primary" disabled={!!invalid || busy} onClick={() => void save()}>
                {busy ? <span className="spinner" /> : <Icon name="check" size={15} />} {t('保存到 Google 日历（{email}）', { email: link.email })}
              </button>
            )}
          </>
        )
      }
    >
      {error ? (
        <div className="notice notice--error">{error}</div>
      ) : !event ? (
        <p className="muted">
          <span className="spinner" /> {t('正在从邮件里找时间…')}
        </p>
      ) : (
        <div className="add-cal__form">
          {suggestion &&
            (candidates.length ? (
              <div className="add-cal__found">
                <span className="field__label">{t('邮件里提到的时间')}</span>
                <div className="add-cal__chips">
                  {candidates.map((c, i) => {
                    const active = c.allDay === event.allDay && c.start === event.start && c.end === event.end;
                    return (
                      <button key={i} className={`add-cal__chip ${active ? 'is-active' : ''}`} title={c.context} aria-pressed={active} onClick={() => set({ allDay: c.allDay, start: c.start, end: c.end })}>
                        {c.deadline && <span className="add-cal__ddl">{t('截止')}</span>}
                        {c.text}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="notice notice--warn">{t('没认出时间，请手动选。')}</div>
            ))}
          <label className="field">
            <span className="field__label">{t('标题')}</span>
            <input value={event.title} onChange={(e) => set({ title: e.target.value })} data-autofocus />
          </label>
          <label className="add-cal__allday">
            <input type="checkbox" checked={event.allDay} onChange={(e) => toggleAllDay(e.target.checked)} /> {t('全天')}
          </label>
          <div className="add-cal__times">
            {event.allDay ? (
              <>
                <label className="field">
                  <span className="field__label">{t('开始日期')}</span>
                  <input
                    type="date"
                    value={event.start}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const days = Math.max(1, Math.round((Date.parse(event.end) - Date.parse(event.start)) / 86_400_000));
                      set({ start: e.target.value, end: addDays(e.target.value, days) });
                    }}
                  />
                </label>
                <label className="field">
                  <span className="field__label">{t('结束日期')}</span>
                  <input type="date" value={addDays(event.end, -1)} min={event.start} onChange={(e) => e.target.value && set({ end: addDays(e.target.value, 1) })} />
                </label>
              </>
            ) : (
              <>
                <label className="field">
                  <span className="field__label">{t('开始')}</span>
                  <input
                    type="datetime-local"
                    value={toLocalInput(event.start)}
                    onChange={(e) => {
                      if (!e.target.value) return;
                      const start = new Date(e.target.value);
                      const length = +new Date(event.end) - +new Date(event.start);
                      set({ start: start.toISOString(), end: new Date(+start + Math.max(0, length)).toISOString() });
                    }}
                  />
                </label>
                <label className="field">
                  <span className="field__label">{t('结束')}</span>
                  <input type="datetime-local" value={toLocalInput(event.end)} onChange={(e) => e.target.value && set({ end: new Date(e.target.value).toISOString() })} />
                </label>
              </>
            )}
          </div>
          <p className="muted add-cal__when">{rangeText(event.allDay, event.start, event.end)}</p>
          <label className="field">
            <span className="field__label">{t('地点')}</span>
            <input value={event.location} onChange={(e) => set({ location: e.target.value })} placeholder={t('可以不填')} />
          </label>
          <label className="field">
            <span className="field__label">{t('说明')}</span>
            <textarea rows={3} value={event.description} onChange={(e) => set({ description: e.target.value })} />
          </label>
          {invalid && <p className="error-text">{invalid}</p>}
          {saved && (
            <div className="notice notice--ok">
              <Icon name="check" size={14} /> {t('已保存到 {calendar} 的 Google 日历。', { calendar: saved.calendar })}
              {saved.htmlLink && (
                <button className="field__link" onClick={() => void api().mail.openExternal(saved.htmlLink!)}>
                  <Icon name="external" size={13} /> {t('在 Google 日历里查看')}
                </button>
              )}
            </div>
          )}
          {info && !info.links.length && (
            <p className="muted add-cal__note">
              <Icon name="lock" size={13} /> {info.google ? t('在 设置 → 日历 里连接 Google 日历后，可以一键保存。') : t('"在 Google 日历中打开"会打开填好的新建页面，点保存就行；也可以下载 .ics 导入别的日历。')}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

// ---------------- Settings → Calendar ----------------

/** CalDAV servers people commonly use (the user can type any other address). */
const CALDAV_PRESETS = (): { id: string; label: string; url: string; userHint: string; passHint: string }[] => [
  { id: 'icloud', label: 'iCloud', url: 'https://caldav.icloud.com', userHint: t('Apple ID 邮箱'), passHint: t('App 专用密码（在 account.apple.com → 登录与安全 里生成）') },
  { id: 'qq', label: t('QQ 邮箱'), url: 'https://dav.qq.com', userHint: t('QQ 邮箱地址'), passHint: t('授权码（QQ 邮箱 → 设置 → 账号 里生成，不是 QQ 密码）') },
  { id: 'fastmail', label: 'Fastmail', url: 'https://caldav.fastmail.com', userHint: t('Fastmail 邮箱地址'), passHint: t('App 密码（Fastmail 设置 → 密码与安全 里生成）') },
  { id: 'other', label: t('其他 CalDAV / Nextcloud'), url: '', userHint: t('用户名'), passHint: t('密码或应用密码') },
];

interface CaldavForm {
  preset: string;
  serverUrl: string;
  username: string;
  password: string;
  name: string;
  /** Re-entering the password of an existing account. */
  account: CalendarAccount | null;
}

export function CalendarSettings() {
  const google = useGoogleInfo();
  const [info, setInfo] = useState<CalendarInfo | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<CaldavForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [colorFor, setColorFor] = useState<string | null>(null);
  const alive = useRef(true);

  const reload = useCallback(async () => {
    try {
      const i = await api().calendar.info();
      if (alive.current) setInfo(i);
    } catch (e) {
      toast(errorText(e), 'error');
    }
  }, []);
  useEffect(() => {
    alive.current = true;
    void reload();
    const off = api().onEvent((e) => {
      if (e.type === 'calendar') void reload();
    });
    return () => {
      alive.current = false;
      off();
    };
  }, [reload]);

  const connectGoogle = async (loginHint?: string) => {
    setBusy(loginHint ?? 'google');
    try {
      const r = await startGoogleFlow({ purpose: 'calendar', ...(loginHint ? { loginHint } : {}) });
      if (r) await reload();
    } finally {
      setBusy(null);
    }
  };

  const sync = async (a: CalendarAccount) => {
    setBusy(a.id);
    try {
      await api().calendar.syncAccount(a.id);
      toast(t('{name} 已同步', { name: a.name }), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(null);
      await reload();
    }
  };

  const remove = async (a: CalendarAccount) => {
    const choice = await confirmDialog({
      title: t('移除日历账户"{name}"？', { name: a.name }),
      message: t('只是不再在馍馍邮箱里显示这个账户的日历，{provider} 上的日历和活动不受影响。邮箱账户也不受影响。', { provider: a.provider === 'google' ? 'Google' : 'CalDAV' }),
      confirm: t('移除'),
      danger: true,
    });
    if (choice !== 'confirm') return;
    try {
      await api().calendar.removeAccount(a.id);
      toast(t('已移除'), 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    }
    await reload();
  };

  const update = async (c: CalendarSource, patch: { hidden?: boolean; color?: string }) => {
    try {
      await api().calendar.updateCalendar(c.id, patch);
      await reload();
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const openForm = (account: CalendarAccount | null = null) => {
    setFormError(null);
    const preset = account ? (CALDAV_PRESETS().find((p) => p.url && account.serverUrl?.startsWith(p.url))?.id ?? 'other') : 'icloud';
    setForm({ preset, serverUrl: account?.serverUrl ?? CALDAV_PRESETS()[0]!.url, username: account?.email ?? '', password: '', name: account?.name ?? '', account });
  };

  const submit = async () => {
    if (!form) return;
    setBusy('caldav');
    setFormError(null);
    try {
      const a = await api().calendar.connectCaldav({ serverUrl: form.serverUrl.trim(), username: form.username.trim(), password: form.password, ...(form.name.trim() ? { name: form.name.trim() } : {}) });
      toast(form.account ? t('{name} 已重新登录', { name: a.name }) : t('已添加 {name}', { name: a.name }), 'ok');
      setForm(null);
      await reload();
    } catch (e) {
      setFormError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const configured = info?.google ?? google?.google ?? false;
  const accounts = info?.accounts ?? [];
  const preset = form ? CALDAV_PRESETS().find((p) => p.id === form.preset) : undefined;
  return (
    <div className="settings__section">
      <h3 className="settings__h">{t('日历账户')}</h3>
      <p className="muted setting__desc">
        {t('连接 Google 日历或 CalDAV 日历（iCloud、QQ 邮箱、Fastmail、Nextcloud 等），所有日历合在「日历」页里一起看。日历账户和邮箱账户互不影响：同一个 Gmail 可以只当邮箱、只当日历，或者两样都用。')}
      </p>
      {!info ? (
        <span className="spinner" />
      ) : (
        <div className="conn-form">
          {accounts.length > 0 && (
            <ul className="account-cards cal-accounts">
              {accounts.map((a) => {
                const own = (info.calendars ?? []).filter((c) => c.accountId === a.id);
                const status = a.error ? tMessage(a.error) : a.lastSync ? t('{ago}同步过', { ago: timeAgo(a.lastSync) }) : t('正在第一次同步…');
                return (
                  <li key={a.id} className="account-card cal-account">
                    <div className="cal-account__head">
                      <Icon name={a.provider === 'google' ? 'calendar' : 'layers'} size={18} />
                      <div className="account-card__text">
                        <strong>{a.name}</strong>
                        <span className="muted">{a.provider === 'google' ? `Google · ${a.email}` : `CalDAV · ${a.serverUrl ? new URL(a.serverUrl).host : ''}`}</span>
                        <span className={`account-card__status ${a.error ? 'is-bad' : ''}`}>{status}</span>
                      </div>
                      {a.needsAuth && (
                        <button className="btn btn--small btn--primary" disabled={!!busy} onClick={() => (a.provider === 'google' ? void connectGoogle(a.email) : openForm(a))}>
                          {busy === a.email ? <span className="spinner" /> : null} {t('重新登录')}
                        </button>
                      )}
                      <button className="btn btn--small btn--quiet" disabled={!!busy} onClick={() => void sync(a)} title={t('立即同步')}>
                        {busy === a.id ? <span className="spinner" /> : <Icon name="refresh" size={14} />} {t('同步')}
                      </button>
                      <button className="btn btn--small btn--quiet tool--danger" disabled={!!busy} onClick={() => void remove(a)}>
                        {t('移除')}
                      </button>
                    </div>
                    {own.length > 0 && (
                      <ul className="cal-account__cals">
                        {own.map((c) => (
                          <li key={c.id}>
                            <button className="cal-account__color" style={{ background: c.color }} aria-label={t('换颜色')} title={t('换颜色')} onClick={() => setColorFor(colorFor === c.id ? null : c.id)} />
                            <span className="cal-account__name">{c.name}</span>
                            {c.readOnly && <span className="tag tag--dim">{t('只读')}</span>}
                            {colorFor === c.id && (
                              <span className="cal-account__swatches">
                                {CALENDAR_SWATCHES.map((col) => (
                                  <button
                                    key={col}
                                    className={`calside__swatch ${col === c.color ? 'is-on' : ''}`}
                                    style={{ background: col }}
                                    aria-label={col}
                                    onClick={() => {
                                      setColorFor(null);
                                      void update(c, { color: col });
                                    }}
                                  />
                                ))}
                              </span>
                            )}
                            <button className="switch" role="switch" aria-checked={!c.hidden} aria-label={t('在日历里显示 {name}', { name: c.name })} title={c.hidden ? t('已隐藏') : t('显示中')} onClick={() => void update(c, { hidden: !c.hidden })} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <div className="cal-connect">
            {configured && (
              <button className="btn btn--primary" disabled={!!busy} onClick={() => void connectGoogle()}>
                {busy === 'google' ? <span className="spinner" /> : <Icon name="calendarPlus" size={16} />} {t('连接 Google 日历')}
              </button>
            )}
            <button className={`btn ${configured ? '' : 'btn--primary'}`} disabled={!!busy} onClick={() => openForm()}>
              <Icon name="plus" size={16} /> {t('添加 CalDAV 日历')}
            </button>
          </div>
          {!configured && (
            <p className="muted setting__desc">
              <Icon name="alert" size={13} /> {t('服务器没有配置 Google 登录，所以不能连接 Google 日历（请管理员按 deploy/README.md 配置）。CalDAV 日历照样能用。')}
            </p>
          )}
          {configured && <p className="muted setting__desc">{t('Google 日历会申请"查看、编辑日历"的权限，只用来显示和修改你的日程。以前连接过的需要重新登录一次。')}</p>}
          {form && (
            <form
              className="cal-caldav"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <h4>{form.account ? t('重新登录 {name}', { name: form.account.name }) : t('添加 CalDAV 日历')}</h4>
              {!form.account && (
                <div className="segmented cal-caldav__presets" role="group">
                  {CALDAV_PRESETS().map((p) => (
                    <button key={p.id} type="button" aria-pressed={form.preset === p.id} onClick={() => setForm({ ...form, preset: p.id, serverUrl: p.url })}>
                      {p.label}
                    </button>
                  ))}
                </div>
              )}
              <label className="field">
                <span className="field__label">{t('服务器地址')}</span>
                <input value={form.serverUrl} disabled={!!form.account} onChange={(e) => setForm({ ...form, serverUrl: e.target.value })} placeholder="https://cloud.example.com/remote.php/dav" required />
              </label>
              <div className="form-grid">
                <label className="field">
                  <span className="field__label">{preset?.userHint ?? t('用户名')}</span>
                  <input value={form.username} disabled={!!form.account} autoComplete="username" onChange={(e) => setForm({ ...form, username: e.target.value })} required data-autofocus={!form.account || undefined} />
                </label>
                <label className="field">
                  <span className="field__label">{t('密码')}</span>
                  <input type="password" value={form.password} autoComplete="current-password" onChange={(e) => setForm({ ...form, password: e.target.value })} required data-autofocus={form.account ? true : undefined} />
                </label>
              </div>
              {preset && <p className="muted setting__desc">{preset.passHint}</p>}
              {!form.account && (
                <label className="field">
                  <span className="field__label">{t('显示名称（可以不填）')}</span>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>
              )}
              {formError && <div className="notice notice--error">{formError}</div>}
              <div className="cal-caldav__actions">
                <button type="button" className="btn btn--quiet" onClick={() => setForm(null)}>
                  {t('取消')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={busy === 'caldav' || !form.serverUrl.trim() || !form.username.trim() || !form.password}>
                  {busy === 'caldav' ? <span className="spinner" /> : <Icon name="check" size={15} />} {form.account ? t('重新登录') : t('连接')}
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

const CALENDAR_SWATCHES = ['#5b3df5', '#ff4f6d', '#1fc98f', '#17b6d6', '#f5b800', '#ff8a3d', '#c04bd8', '#5d5987'];
